import React, { useRef, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import currentTags from "./currentTags";
import "./Search.css";
import Tag from "./Tag";
import Cards from "./Cards";
import { searchQueryKey, useSearchQuery } from "../queries/search";
import { nearbyQueryKey, useNearbyQuery } from "../queries/nearby";
import { getDistanceBetweenLocations } from "./placeUtils";

const maxLengthText = 100;
const nearbyRadii = [1000, 3000, 5000, 10000];
const nearbyLocationStorageKey = "wolne-nearby-location";

function parseCategory(value) {
  return value === "eventy" ? "eventy" : "miejsca";
}

function parseTagIds(value) {
  return (value ?? "")
    .split(",")
    .map((id) => parseInt(id, 10))
    .filter((id) => !Number.isNaN(id));
}

function parseNearbyRadius(value) {
  const radius = parseInt(value, 10);
  return nearbyRadii.includes(radius) ? radius : 5000;
}

function getStoredNearbyLocation() {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(nearbyLocationStorageKey));
    if (Number.isFinite(stored?.lat) && Number.isFinite(stored?.lng)) return stored;
  } catch (error) {
  }

  return null;
}

function Search(props) {
  const ref = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const category = parseCategory(searchParams.get("kategoria"));
  const urlText = searchParams.get("tekst") ?? "";
  const urlTags = currentTags.filter((tag) => parseTagIds(searchParams.get("tagi")).includes(tag.id));
  const urlIncludeDescription = searchParams.get("opis") === "1";
  const isNearbyMode = searchParams.get("blisko") === "1";
  const nearbyRadius = parseNearbyRadius(searchParams.get("promien"));
  const trimmedUrlText = urlText.trim();
  const hasTextCriteria = trimmedUrlText.length > 0 || urlTags.length > 0;

  const [draftText, setDraftText] = useState(urlText);
  const [draftTags, setDraftTags] = useState(urlTags);
  const [draftIncludeDescription, setDraftIncludeDescription] = useState(urlIncludeDescription);
  const [nearbyLocation, setNearbyLocation] = useState(getStoredNearbyLocation);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [nearbyLocationError, setNearbyLocationError] = useState("");

  // Cached by react-query (same cache the category pages use), so navigating back to a
  // previous search from a profile page restores instantly instead of refetching.
  const searchQuery = useSearchQuery({
    addressIp: props.addressIp,
    text: trimmedUrlText,
    tags: urlTags,
    category,
    includeDescription: urlIncludeDescription,
    enabled: hasTextCriteria && !isNearbyMode,
  });
  const nearbyQuery = useNearbyQuery({
    category,
    location: nearbyLocation,
    radius: nearbyRadius,
    enabled: isNearbyMode && nearbyLocation !== null,
  });
  const nearbyResults = category === "eventy"
    ? (nearbyQuery.data?.events ?? [])
    : (nearbyQuery.data?.places ?? []);
  const results = isNearbyMode
    ? nearbyResults.map((item) => ({
      ...item,
      distanceFromUser: getDistanceBetweenLocations(nearbyLocation, item.location),
    }))
    : (hasTextCriteria ? (searchQuery.data ?? []) : []);
  const queryKey = isNearbyMode
    ? nearbyQueryKey(category, nearbyLocation?.lat, nearbyLocation?.lng, nearbyRadius)
    : searchQueryKey(category, trimmedUrlText, urlTags.map((tag) => tag.id), urlIncludeDescription);
  const setResults = (next) => {
    if (!isNearbyMode) {
      queryClient.setQueryData(queryKey, next);
      return;
    }

    const resultKey = category === "eventy" ? "events" : "places";
    queryClient.setQueryData(queryKey, (current = {}) => ({
      ...current,
      [resultKey]: typeof next === "function" ? next(current[resultKey] ?? []) : next,
    }));
  };

  // The page number lives in the URL too (like the rest of the search criteria), so it
  // survives a remount on back navigation instead of resetting to page 1.
  const urlPageNumber = Math.max(0, (parseInt(searchParams.get("strona"), 10) || 1) - 1);

  function setPageNumberInUrl(nextPageNumber) {
    const next = new URLSearchParams(searchParams);
    if (nextPageNumber > 0) {
      next.set("strona", String(nextPageNumber + 1));
    } else {
      next.delete("strona");
    }
    setSearchParams(next, { replace: true });
  }

  // Scroll position for this page is handled globally by useScrollRestoration
  // (see ScrollManager) - forcing a scroll-to-top here would fight it on back navigation.

  // Keep the editable fields in sync when the URL changes from elsewhere (back/forward navigation, a shared link).
  useEffect(() => {
    setDraftText(urlText);
    setDraftTags(urlTags);
    setDraftIncludeDescription(urlIncludeDescription);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  function submitSearch() {
    const trimmed = draftText.trim();
    if (!trimmed && draftTags.length === 0) return;
    if (trimmed.length > maxLengthText) return;

    const next = new URLSearchParams();
    next.set("kategoria", category);
    if (trimmed) next.set("tekst", trimmed);
    if (draftTags.length) next.set("tagi", draftTags.map((tag) => tag.id).join(","));
    if (trimmed && draftIncludeDescription) next.set("opis", "1");
    setSearchParams(next);
  }

  function handleCategoryChange(value) {
    if (value === category) return;
    const next = new URLSearchParams();
    next.set("kategoria", value);
    if (isNearbyMode) {
      next.set("blisko", "1");
      next.set("promien", String(nearbyRadius));
    }
    setSearchParams(next);
  }

  function requestNearbyItems() {
    if (!navigator.geolocation) {
      setNearbyLocationError("Twoja przeglądarka nie obsługuje lokalizacji.");
      return;
    }

    setIsRequestingLocation(true);
    setNearbyLocationError("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = { lat: position.coords.latitude, lng: position.coords.longitude };
        try {
          window.sessionStorage.setItem(nearbyLocationStorageKey, JSON.stringify(location));
        } catch (error) {
        }
        setNearbyLocation(location);
        setNearbyLocationError("");
        setIsRequestingLocation(false);
        setSearchParams({
          kategoria: category,
          blisko: "1",
          promien: String(nearbyRadius),
        });
      },
      (error) => {
        setIsRequestingLocation(false);
        if (!nearbyLocation) {
          setNearbyLocationError(error.code === error.PERMISSION_DENIED
            ? "Aby pokazać pobliskie propozycje, zezwól przeglądarce na dostęp do lokalizacji."
            : "Nie udało się ustalić lokalizacji. Spróbuj ponownie.");
        }
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  }

  function disableNearbyMode() {
    const next = new URLSearchParams(searchParams);
    next.delete("blisko");
    next.delete("promien");
    next.delete("strona");
    setSearchParams(next);
  }

  function setNearbyRadius(radius) {
    const next = new URLSearchParams(searchParams);
    next.set("promien", String(radius));
    next.delete("strona");
    setSearchParams(next, { replace: true });
  }

  function tagClicked(id) {
    if (draftTags.some((tag) => tag.id === id)) return;
    const tag = currentTags.find((item) => item.id === id);
    if (tag) setDraftTags([...draftTags, tag]);
  }

  function removeTag(indexToRemove) {
    setDraftTags(draftTags.filter((_, index) => index !== indexToRemove));
  }

  function scrollToElement() {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  }

  const hasDraftCriteria = draftText.trim().length > 0 || draftTags.length > 0;
  const textTooLong = draftText.length > maxLengthText;
  const activeQuery = isNearbyMode ? nearbyQuery : searchQuery;
  const hasActiveCriteria = isNearbyMode || hasTextCriteria;

  return (
    <div className="search-page">
      <div className="search-form">
        <div className="search-breadcrumb" role="navigation" aria-label="breadcrumb">
          <Link to="/">Strona główna</Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">Szukaj</span>
        </div>
        <h1>Szukaj</h1>
        <p className="search-subtitle">Znajdź miejsca i wydarzenia w Krakowie — po nazwie, opisie albo kategorii.</p>

        <div className="search-panel">
          <div className="search-category-toggle" role="tablist" aria-label="Kategoria wyszukiwania">
            <button
              type="button"
              role="tab"
              aria-selected={category === "miejsca"}
              className={"search-category-btn" + (category === "miejsca" ? " is-active" : "")}
              onClick={() => handleCategoryChange("miejsca")}
            >
              Miejsca
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={category === "eventy"}
              className={"search-category-btn" + (category === "eventy" ? " is-active" : "")}
              onClick={() => handleCategoryChange("eventy")}
            >
              Eventy
            </button>
          </div>

          <div className="search-nearby-row">
            <div>
              <p>W pobliżu mnie</p>
              <span>Użyj lokalizacji, aby zobaczyć najbliższe {category === "eventy" ? "eventy" : "miejsca"}.</span>
            </div>
            {isNearbyMode && nearbyLocation ? (
              <button type="button" className="search-nearby-toggle is-active" onClick={disableNearbyMode}>
                Lokalizacja włączona
              </button>
            ) : (
              <button type="button" className="search-nearby-toggle" onClick={requestNearbyItems} disabled={isRequestingLocation}>
                {isRequestingLocation ? "Sprawdzamy…" : "Włącz lokalizację"}
              </button>
            )}
          </div>

          {isNearbyMode ? (
            <div className="search-nearby-radius" aria-label="Promień wyszukiwania">
              <span>Promień:</span>
              {nearbyRadii.map((radius) => (
                <button type="button" key={radius} className={nearbyRadius === radius ? "is-active" : ""} onClick={() => setNearbyRadius(radius)}>
                  {radius / 1000} km
                </button>
              ))}
            </div>
          ) : null}

          {nearbyLocationError ? <p className="search-nearby-error" role="alert">{nearbyLocationError}</p> : null}

          <div className={"search-text-controls" + (isNearbyMode ? " is-disabled" : "")}>
          <div className="search-row">
            <div className="search-row-input">
              <input
                className="search-text-input"
                type="text"
                placeholder="Wpisz szukaną frazę, np. nazwę miejsca…"
                maxLength={maxLengthText}
                disabled={isNearbyMode}
                onKeyUp={(event) => {
                  if (event.key === "Enter") submitSearch();
                }}
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
              />
              <span className={"search-text-counter" + (textTooLong ? " is-over-limit" : "")}>
                {draftText.length}/{maxLengthText}
              </span>
            </div>
            <button
              onClick={submitSearch}
              className="btn search-submit-btn"
              disabled={isNearbyMode || !hasDraftCriteria || textTooLong}
            >
              Szukaj
            </button>
          </div>

          <label className="search-description-toggle">
            <input
              type="checkbox"
              disabled={isNearbyMode}
              checked={draftIncludeDescription}
              onChange={(e) => setDraftIncludeDescription(e.target.checked)}
            />
            Szukaj też w opisach
          </label>

          <div className="search-tags-block">
            <p className="search-tags-label">Zawęź tagami (opcjonalnie)</p>
            <div className="search-section-tags">
              {currentTags
                .filter((item) => item.category === category)
                .map((tag) => (
                  <Tag
                    key={tag.id}
                    id={tag.id}
                    tagName={tag.name}
                    clickFunction={tagClicked}
                    isSelected={draftTags.some((item) => item.id === tag.id)}
                  />
                ))}
            </div>

            {draftTags.length > 0 && (
              <ul id="search-tags">
                {draftTags.map((tag, index) => (
                  <li key={tag.id} className="search-tag">
                    <span className="search-tag-title">{tag.name}</span>
                    <span
                      className="search-tag-close-icon"
                      role="button"
                      aria-label={`Usuń tag ${tag.name}`}
                      onClick={() => removeTag(index)}
                    >
                      ×
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          </div>
        </div>
      </div>

      <div className="results-section-pointer" ref={ref}></div>
      <div className="results-section">
        {!hasActiveCriteria ? null : isNearbyMode && !nearbyLocation ? (
          <div className="search-feedback">
            <p>Włącz lokalizację, aby zobaczyć pobliskie propozycje.</p>
          </div>
        ) : activeQuery.isLoading ? (
          <div className="loader"></div>
        ) : activeQuery.isError ? (
          <div className="search-feedback search-feedback-error">
            <p>Nie udało się pobrać wyników. Sprawdź połączenie i spróbuj ponownie.</p>
          </div>
        ) : results.length > 0 ? (
          <Cards
            places={results}
            setPlaces={setResults}
            cardsCategory={category === "eventy" ? "events" : "places"}
            category="Wyniki"
            showCategoryTitle={false}
            loading={false}
            setLoading={() => {}}
            pageNumber={urlPageNumber}
            setPageNumber={setPageNumberInUrl}
            search={scrollToElement}
            preserveOrder={isNearbyMode}
            backContext={{ label: "Szukaj", path: `/szukaj?${searchParams.toString()}` }}
          />
        ) : (
          <div className="search-feedback search-feedback-empty">
            <p>Brak wyników dla podanych kryteriów.</p>
            <p>Spróbuj innych tagów lub innej frazy.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Search;
